import { useEffect, useState } from "react";
import { handshake } from "../api/auth.js";
import { useAuthStore } from "../store/authStore.js";
import { parseApiError } from "../api/http.js";
import type { BaseContext } from "./useBaseContext.js";

export type HandshakeStatus =
  | { phase: "loading" }
  | { phase: "needs_setup" }
  | { phase: "ready" }
  | { phase: "error"; message: string };

/** handshake 一次，把结果灌进 authStore；上层根据 phase 决定渲染 SetupWizard 还是 Dashboard。 */
export function useHandshake(ctx: BaseContext | null): HandshakeStatus {
  const [status, setStatus] = useState<HandshakeStatus>({ phase: "loading" });

  useEffect(() => {
    if (!ctx) return;
    let cancelled = false;

    const run = async (): Promise<void> => {
      try {
        const auth = useAuthStore.getState();
        auth.setBaseContext({
          appToken: ctx.appToken,
          userId: ctx.userId,
          tenantKey: ctx.tenantKey,
        });
        const resp = await handshake({
          app_token: ctx.appToken,
          user_id: ctx.userId,
          ...(ctx.tenantKey && { tenant_key: ctx.tenantKey }),
        });
        if (cancelled) return;
        if (resp.needs_setup) {
          auth.setSetupToken(resp.setup_token);
          setStatus({ phase: "needs_setup" });
        } else {
          auth.setSession({
            installId: resp.install_id,
            sessionToken: resp.session_token,
            displayName: resp.display_name,
            enabledPlatforms: resp.enabled_platforms,
            hasPat: resp.has_pat,
            hasRobotWebhook: resp.has_robot_webhook,
          });
          setStatus({ phase: "ready" });
        }
      } catch (err) {
        if (cancelled) return;
        setStatus({ phase: "error", message: parseApiError(err).message });
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [ctx?.appToken, ctx?.userId, ctx?.tenantKey]);

  return status;
}
